/**
 * FilterChip Component
 *
 * Displays an active or current filter as a styled MUI Chip.
 * Supports two variants:
 * - Active: Completed filter with value (deletable)
 * - Current: Filter being edited (shows partial state)
 */

"use client";

import React from "react";
import { Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Props for the FilterChip component
 */
interface FilterChipProps {
  /** Text to display in the chip */
  label: string;
  /** Callback when delete button is clicked */
  onDelete: () => void;
  /** Visual variant of the chip */
  variant: "active" | "current";
  /**
   * Whether the current filter is waiting for sub-option selection.
   * Only used when variant is "current".
   */
  isWaitingForSubOption?: boolean;
}

/**
 * FilterChip renders a styled chip for displaying filter state.
 *
 * @param props - Component props
 * @returns Rendered chip element
 *
 * @example
 * // Active filter chip
 * <FilterChip
 *   label="Name=John"
 *   onDelete={() => removeFilter("name")}
 *   variant="active"
 * />
 *
 * @example
 * // Current filter being edited
 * <FilterChip
 *   label="Price..."
 *   onDelete={() => clearCurrentFilter()}
 *   variant="current"
 *   isWaitingForSubOption={true}
 * />
 */
export function FilterChip({
  label,
  onDelete,
  variant,
  isWaitingForSubOption = false,
}: FilterChipProps) {
  // Determine colors based on variant and state
  const isActive = variant === "active";
  const showWarningColor = !isActive && isWaitingForSubOption;

  return (
    <Chip
      label={label}
      onDelete={onDelete}
      deleteIcon={<CloseIcon sx={{ fontSize: isActive ? 16 : 14 }} />}
      sx={{
        // Background color: primary for active, warning for waiting, light for current
        bgcolor: isActive ? "primary.main" : showWarningColor ? "warning.light" : "primary.light",
        // Text color matches background
        color: isActive
          ? "primary.contrastText"
          : showWarningColor
          ? "warning.contrastText"
          : "primary.contrastText",
        fontWeight: 500,
        // Slightly less padding on right for current chips
        pr: isActive ? undefined : 0.5,
        "& .MuiChip-label": {
          pr: isActive ? undefined : 0.5,
        },
        "& .MuiChip-deleteIcon": {
          color: isActive ? "primary.contrastText" : "inherit",
          opacity: isActive ? undefined : 0.7,
          "&:hover": {
            color: isActive ? "primary.contrastText" : undefined,
            opacity: isActive ? 0.7 : 1,
          },
        },
      }}
    />
  );
}
